import React from 'react';
import { Link } from 'react-router-dom';
import { getClientDownloads, getMacQuarantineCommand } from '../utils/clientDownloads';
import { useLocale, useLocalizedPath } from '../hooks/useLocale';

interface ClientDownloadCardsProps {
  showMacQuarantineTip?: boolean;
  onCopyMacCommand?: () => void;
  compact?: boolean;
  iosHref?: string;
}

const WindowsIcon = () => (
  <svg className="h-8 w-8 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
  </svg>
);

const MacIcon = () => (
  <svg className="h-8 w-8 text-gray-900 dark:text-gray-100" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const AndroidIcon = () => (
  <svg className="h-8 w-8 text-green-500" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.61 15.15c-.46 0-.84-.37-.84-.83s.37-.84.84-.84c.46 0 .84.37.84.84s-.37.83-.84.83m-9.2 0c-.46 0-.84-.37-.84-.83s.37-.84.84-.84c.46 0 .84.37.84.84s-.37.83-.84.83m9.5-6.92l1.67-2.9c.09-.17.03-.38-.13-.47-.17-.09-.38-.03-.47.13l-1.69 2.93A9.973 9.973 0 0012 7.2c-1.63 0-3.16.39-4.52 1.08L5.8 5.35c-.09-.17-.3-.22-.47-.13-.17.09-.22.3-.13.47l1.67 2.9C4.35 10.34 2.8 13.05 2.8 16h18.4c0-2.95-1.56-5.66-4.29-7.77M7.84 13.5h-.02c-.65 0-1.17-.52-1.17-1.17s.52-1.16 1.17-1.16 1.16.52 1.16 1.16c0 .65-.51 1.17-1.14 1.17m8.38 0c-.65 0-1.17-.52-1.17-1.17s.52-1.16 1.17-1.16 1.16.52 1.16 1.16c0 .65-.52 1.17-1.16 1.17z" />
  </svg>
);

const IosIcon = () => (
  <svg className="h-8 w-8 text-gray-700 dark:text-gray-300" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

export const ClientDownloadCards: React.FC<ClientDownloadCardsProps> = ({
  showMacQuarantineTip = false,
  onCopyMacCommand,
  compact = false,
  iosHref = '/help',
}) => {
  const downloads = getClientDownloads();
  const locale = useLocale();
  const localizedPath = useLocalizedPath();
  const cardClass = compact
    ? 'flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors'
    : 'flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors';

  const handleCopyMacCommand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const command = getMacQuarantineCommand();
    navigator.clipboard.writeText(command).then(() => {
      onCopyMacCommand?.();
    });
  };

  const items = [
    { key: 'windows' as const, icon: <WindowsIcon />, data: downloads.windows, external: true },
    { key: 'macos' as const, icon: <MacIcon />, data: downloads.macos, external: true },
    { key: 'android' as const, icon: <AndroidIcon />, data: downloads.android, external: true },
    { key: 'ios' as const, icon: <IosIcon />, data: downloads.ios, external: false },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map(({ key, icon, data, external }) => {
        const content = (
          <>
            {icon}
            <div className="ml-4 min-w-0">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">{data.label}</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">{data.description}</p>
              {key === 'macos' && showMacQuarantineTip && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {locale === 'zh'
                      ? '安装后若无法打开，请在终端执行：'
                      : 'If blocked after install, run in Terminal:'}
                  </p>
                  <button
                    onClick={handleCopyMacCommand}
                    className="text-xs bg-primary-600 hover:bg-primary-700 text-white px-2 py-1 rounded transition-colors"
                  >
                    {locale === 'zh' ? '复制命令' : 'Copy command'}
                  </button>
                </div>
              )}
            </div>
          </>
        );

        if (external) {
          return (
            <a
              key={key}
              href={data.url}
              className={cardClass}
              target="_blank"
              rel="noopener noreferrer"
            >
              {content}
            </a>
          );
        }

        return (
          <Link key={key} to={localizedPath(iosHref)} className={cardClass}>
            {content}
          </Link>
        );
      })}
    </div>
  );
};
